import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  feedbackService,
  CreateFeedbackDto,
  UpdateFeedbackDto,
  SubmitResponseDto,
  FeedbackStructure,
} from '../services/feedbackService';

export const useFeedbackList = () => {
  return useQuery({
    queryKey: ['feedback', 'list'],
    queryFn: () => feedbackService.getFeedbacks(),
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useFeedbackById = (id: string | undefined) => {
  return useQuery({
    queryKey: ['feedback', 'detail', id],
    queryFn: () => feedbackService.getFeedbackById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeedbackJs = (id: string | undefined) => {
  return useQuery({
    queryKey: ['feedback', 'surveyjs', id],
    queryFn: () => feedbackService.getFeedbackJs(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeedbackStructure = (id: string | undefined, versionNo: number) => {
  return useQuery({
    queryKey: ['feedback', 'structure', id, versionNo],
    queryFn: () => feedbackService.getFeedbackStructure(id!, versionNo),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeedbackVersions = (id: string | undefined) => {
  return useQuery({
    queryKey: ['feedback', 'versions', id],
    queryFn: () => feedbackService.getFeedbackVersions(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeedbackResponses = (feedbackId: string | undefined) => {
  return useQuery({
    queryKey: ['feedback', 'responses', feedbackId],
    queryFn: () => feedbackService.getFeedbackResponses(feedbackId!),
    enabled: !!feedbackId,
    staleTime: 30 * 1000,
  });
};

export const useCreateFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateFeedbackDto) => feedbackService.createFeedback(dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'list'] });
    },
  });
};

export const useUpdateFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateFeedbackDto }) =>
      feedbackService.updateFeedback(id, dto),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'list'] });
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'detail', data.id] });
    },
  });
};

export const useDeleteFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => feedbackService.deleteFeedback(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
};

export const useUpdateFeedbackStructure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      versionNo,
      structure,
    }: {
      id: string;
      versionNo: number;
      structure: FeedbackStructure;
    }) => feedbackService.updateFeedbackStructure(id, versionNo, structure),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'structure', variables.id, variables.versionNo] });
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'surveyjs', variables.id] });
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'versions', variables.id] });
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'list'] });
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'detail', variables.id] });
    },
  });
};

export const useSubmitResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ feedbackId, dto }: { feedbackId: string; dto: SubmitResponseDto }) =>
      feedbackService.submitResponse(feedbackId, dto),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'responses', variables.feedbackId] });
      await queryClient.invalidateQueries({ queryKey: ['feedback-instances', 'my-feedback'] });
    },
  });
};

export const usePublishVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, versionNo }: { id: string; versionNo: number }) =>
      feedbackService.publishVersion(id, versionNo),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['feedback', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['feedback', 'detail', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['feedback', 'versions', variables.id] });
    },
  });
};

